import React, { useState } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

export default function Table({ columns, data, onRowClick, emptyText = 'Нет данных', pageSize = 10, loading }) {
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const sorted = [...(data || [])].sort((a, b) => {
    if (!sortKey) return 0;
    const av = a[sortKey], bv = b[sortKey];
    if (av === bv) return 0;
    const res = av > bv ? 1 : -1;
    return sortDir === 'asc' ? res : -res;
  });

  const totalPages = Math.ceil(sorted.length / pageSize);
  const paged = sorted.slice(page * pageSize, (page + 1) * pageSize);

  const handleSort = (key) => {
    if (!key) return;
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setPage(0);
  };

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F8F9FB' }}>
              {columns.map(col => (
                <th key={col.key} onClick={() => col.sortable !== false && handleSort(col.key)}
                  style={{
                    padding: '11px 16px', textAlign: 'left', fontWeight: 600,
                    color: '#6B7280', fontSize: 12, letterSpacing: '0.03em',
                    cursor: col.sortable !== false ? 'pointer' : 'default',
                    whiteSpace: 'nowrap', userSelect: 'none',
                    borderBottom: '1px solid #F0F0F0',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {col.label}
                    {col.sortable !== false && (
                      <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <ChevronUp size={10} style={{ opacity: sortKey === col.key && sortDir === 'asc' ? 1 : 0.3 }} />
                        <ChevronDown size={10} style={{ opacity: sortKey === col.key && sortDir === 'desc' ? 1 : 0.3, marginTop: -4 }} />
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {columns.map(c => (
                    <td key={c.key} style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6' }}>
                      <div style={{ height: 14, background: '#F3F4F6', borderRadius: 4, width: '70%', animation: 'shimmer 1.5s infinite' }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : paged.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ padding: '40px 16px', textAlign: 'center', color: '#9CA3AF' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
                  {emptyText}
                </td>
              </tr>
            ) : (
              paged.map((row, i) => (
                <tr key={row.id || i}
                  onClick={() => onRowClick && onRowClick(row)}
                  style={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    transition: 'background 0.12s',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                  onMouseEnter={e => { if (onRowClick) e.currentTarget.style.background = '#F8F9FB'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                  {columns.map(col => (
                    <td key={col.key} style={{ padding: '12px 16px', color: '#374151', verticalAlign: 'middle' }}>
                      {col.render ? col.render(row[col.key], row) : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid #F0F0F0' }}>
          <span style={{ fontSize: 12, color: '#6B7280' }}>
            {page * pageSize + 1}–{Math.min((page + 1) * pageSize, sorted.length)} из {sorted.length}
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              style={{ padding: '5px 8px', border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff', cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.4 : 1 }}>
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(0, Math.min(page - 2, totalPages - 5)) + i;
              return (
                <button key={p} onClick={() => setPage(p)}
                  style={{ padding: '5px 10px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 12, fontWeight: p === page ? 700 : 400, background: p === page ? '#4A6CF7' : '#fff', color: p === page ? '#fff' : '#374151', cursor: 'pointer' }}>
                  {p + 1}
                </button>
              );
            })}
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              style={{ padding: '5px 8px', border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: page >= totalPages - 1 ? 0.4 : 1 }}>
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
